import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { cloneDeep } from 'lodash';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import { OCR_CONTAINER_BLOCK } from 'constants/feature/statement-content-constants';
import Notification from 'components/common/notification-component';
import { ReactComponent as CloseButton } from 'icons/close-button-red.svg';
import { ReactComponent as AnnotationIndicator } from 'icons/annotation-indicator.svg';
import {
  isElementReportPreviewPage,
  getCalloutVisibilityDetails,
  getInitialCalloutVisibilityDetails,
} from 'utils/ocr-annotation-utils';
import {
  getMarkerPosition,
  getCalloutPosition,
  hasAnnotations,
} from 'utils/ocr-annotation-utils';
import { updateSideBySideElementCacheByCallouts } from 'store/actions/statement-content-annotation-creation-actions';
import AnnotationCalloutComponent from './tieout-element-common/annotation-callout-component';
import { setSelectedElementDetails } from 'store/actions/report-preview-page-actions';
import { updateElementCacheByCalloutPosition } from 'store/actions/element-cache-actions';
import {
  replaceSelectedElementWithoutRemovingOld,
  clearSelectedElementsContentPanel,
  resetToPreviousSelectedElementMap,
} from 'store/actions/action-creators';
import { setCalloutArrowDynamicWidth } from 'constants/feature/tieout-element-utils';
import { getElementBoundaryInfo } from 'utils/ocr-element-utils';
import DropIndicator from 'components/feature/statement-content-panel/ocr-callouts-drop-indicator';
import { getDraggedCalloutPosition } from 'utils/ocr-annotation-utils';
import { REVERSE_ANNOTATION_POSITION_TYPES } from 'constants/feature/annotation-elements-constants';
import { ELEMENT_HIGHLIGHT_STATES } from 'constants/feature/tieout-element-constants';
import { saveCalloutPositions } from 'api/element-api';
import { isNullOrUndefined } from 'utils/object-utils';
import { setOcrCurrentViewPageNumber } from 'store/actions/ocr-current-view-page-number-actions';

const OcrCallouts = ({
  elementId,
  BLOCK,
  polygonInPixel,
  elementDetails,
  width,
  children,
  annotationDisplayFilter,
  annotation,
  annotationPosition,
  setAnnotation,
  fontSize,
  canvasID,
  isLeftView = false,
  batchElementList,
  revision,
  selectedElementMap,
  isOCR,
  reportPreviewPage,
}) => {
  const isMouseMoved = useRef(false);
  const dispatch = useDispatch();
  const url = useLocation().pathname;
  const isPreviewPage = isElementReportPreviewPage(url);
  const isSideBySideView = url.includes(ROUTE_CONSTANTS.SIDE_BY_SIDE);
  const { position } = annotationPosition;
  const { selectedElementIds, selectedElementsMap } = batchElementList;
  const ANNOTATION_INDICATOR_WIDTH = '13px';
  const ANNOTATION_INDICATOR_HEIGHT = '8px';
  const CANVAS_CONTAINER_SCROLLBAR_WIDTH = 20;
  const calloutPosition = !isNullOrUndefined(elementDetails?.calloutPosition)
    ? elementDetails?.calloutPosition.toLowerCase()
    : position;

  let isDraggingInProgress = false;
  let offsetX = 0;
  let offsetY = 0;
  let calloutWidth = 0;
  let boundMouseMove = null;
  let boundMouseUp = null;

  const [isDragging, setDragging] = useState(false);
  const [prevCalloutPosition, setPrevCalloutPosition] = useState(null);
  const [indicatorPositions, setIndicatorPositions] = useState(null);
  const [annotationStyle, setAnnotationStyle] = useState({
    marker: {},
    callout: {},
  });
  const [dragPosition, setDragPosition] = useState({
    draggedX: 0,
    draggedY: 0,
  });
  const [cursorType, setCursorType] = useState({
    move: false,
    notAllowed: false,
  });
  const [prevSelectedElementMap, setPrevSelectedElementMap] = useState(null);

  const handleIndicatorClick = (elementId) => {
    if (isMouseMoved.current) {
      isMouseMoved.current = false;
      return;
    }

    if (isPreviewPage) {
      dispatch(
        setSelectedElementDetails({
          selectedElementId: elementId,
          sectionId: elementDetails.sectionId,
          isClickedOnReport: false,
        }),
      );
      if (
        isOCR &&
        reportPreviewPage?.selectedElementsSectionId !==
          elementDetails.sectionId
      ) {
        dispatch(setOcrCurrentViewPageNumber(elementDetails.sectionId));
      }
    } else {
      const initialCalloutVisibilityDetails = {
        displayMarker: !annotation['marker'],
        displayCallout: !annotation['callout'],
        displayAnnotation: !annotation['display'],
      };

      const { displayCallout } = getCalloutVisibilityDetails(
        initialCalloutVisibilityDetails,
        annotation.annotationsConfig,
        annotationDisplayFilter,
      );

      dispatch(
        updateSideBySideElementCacheByCallouts({
          elementIds: [elementId],
          showCallout: displayCallout,
          isLeftSideView: isLeftView,
        }),
      );
    }
  };

  useEffect(() => {
    if (annotationDisplayFilter.showAll) {
      const initialCalloutVisibilityDetails = {
        displayMarker: false,
        displayCallout: true,
        displayAnnotation: true,
      };

      const calloutVisibilityDetails = getCalloutVisibilityDetails(
        initialCalloutVisibilityDetails,
        annotation.annotationsConfig,
        annotationDisplayFilter,
      );

      setAnnotation({
        ...annotation,
        marker: calloutVisibilityDetails.displayMarker,
        display: calloutVisibilityDetails.displayAnnotation,
        callout: calloutVisibilityDetails.displayCallout,
        showAll: true,
      });
    } else {
      setAnnotation({
        ...annotation,
        display: false,
        marker: isPreviewPage ? annotation.marker : true,
        callout: isPreviewPage ? annotation.callout : false,
        showAll: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationDisplayFilter.showAll]);

  useEffect(() => {
    const initialCalloutVisibilityDetails = getInitialCalloutVisibilityDetails(
      annotation,
      annotationDisplayFilter,
    );

    const calloutVisibilityDetails = getCalloutVisibilityDetails(
      initialCalloutVisibilityDetails,
      annotation.annotationsConfig,
      annotationDisplayFilter,
    );

    setAnnotation({
      ...annotation,
      marker: isPreviewPage
        ? annotation.marker
        : calloutVisibilityDetails.displayMarker,
      display: calloutVisibilityDetails.displayAnnotation,
      callout: isPreviewPage
        ? annotation.callout
        : calloutVisibilityDetails.displayCallout,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    annotationDisplayFilter.workpaper,
    annotationDisplayFilter.tickmarks,
    annotationDisplayFilter.internalRef,
    annotationDisplayFilter.formula,
    annotationDisplayFilter.comfortLetter,
  ]);

  const { polygonHeight } = getElementBoundaryInfo(polygonInPixel);

  useEffect(() => {
    const canvasElement = document.getElementById(canvasID);
    const element = document.getElementById(elementId);
    const { clientHeight, clientWidth } = canvasElement ? canvasElement : {};
    const canvasWidth = clientWidth ? clientWidth : 0;
    const canvasHeight = clientHeight ? clientHeight : 0;
    const offsetWidth = element ? { [elementId]: element.offsetWidth } : {};
    const offsetHeight = element ? { [elementId]: element.offsetHeight } : {};

    const { dynamicWidthBefore } = setCalloutArrowDynamicWidth({
      tieoutElement: element,
      position: calloutPosition,
      fontSize,
    });

    const style = {
      marker: {
        ...getMarkerPosition({
          position: calloutPosition,
          polygonInPixel,
          canvasHeight,
        }),
      },
      callout: {
        ...getCalloutPosition({
          position: calloutPosition,
          polygonInPixel,
          canvasHeight,
          canvasWidth,
          offsetHeight,
          offsetWidth,
          elementId,
          isPreviewPage,
          dynamicWidthBefore,
        }),
      },
    };

    if (elementDetails.marker) {
      setAnnotationStyle(style);
    } else if (
      !elementDetails.marker &&
      Object.keys(offsetHeight).length &&
      Object.keys(offsetWidth).length
    ) {
      setAnnotationStyle(style);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    elementId,
    fontSize,
    annotation,
    annotationDisplayFilter,
    calloutPosition,
    polygonInPixel,
    elementDetails,
    prevCalloutPosition,
  ]);

  useEffect(() => {
    const elementsMap = Object.values(selectedElementMap);
    const hasCalloutDragFailureEntry = elementsMap.some(
      (item) => item.color === ELEMENT_HIGHLIGHT_STATES.CALLOUT_DRAG_FAILURE,
    );
    if (elementsMap.length > 0 && !hasCalloutDragFailureEntry) {
      setPrevSelectedElementMap(cloneDeep(selectedElementMap));
    }
  }, [selectedElementMap]);

  const saveCalloutPositionsInDB = async (payload) => {
    try {
      await saveCalloutPositions(payload, revision.id);
    } catch (error) {
      dispatch(
        updateElementCacheByCalloutPosition({
          elementCalloutPosition: payload,
          shouldReset: true,
        }),
      );
      dispatch(
        replaceSelectedElementWithoutRemovingOld({
          elementIds: Object.keys(payload),
          color: ELEMENT_HIGHLIGHT_STATES.CALLOUT_DRAG_FAILURE,
        }),
      );
      document.addEventListener(
        'click',
        (e) => {
          if (!e.ctrlKey) {
            if (!prevSelectedElementMap) {
              dispatch(clearSelectedElementsContentPanel());
              return;
            }
            dispatch(resetToPreviousSelectedElementMap(prevSelectedElementMap));
          }
        },
        {
          once: true,
        },
      );
      toast.error(
        <Notification
          icon={<CloseButton />}
          message={{
            id: 'element.callout.position.toast-error-message',
            values: { totalElementCount: Object.keys(payload).length },
          }}
          title={{
            id: 'element.callout.position.toast-error-title',
          }}
        />,
        { autoClose: 5000 },
      );
    }
  };

  const handleMouseDown = (e, isMarker, elementId, polygon) => {
    if (isSideBySideView || isPreviewPage) return;
    e.preventDefault();
    isDraggingInProgress = true;
    offsetX = isMarker
      ? e.clientX - e.currentTarget.getBoundingClientRect().left
      : e.clientX - e.currentTarget.getBoundingClientRect().right;
    offsetY = e.clientY - e.currentTarget.getBoundingClientRect().bottom;
    calloutWidth = e.currentTarget.getBoundingClientRect().width;

    //calculate the positions of all 4 drop indicators
    const canvasHeight = document
      .querySelector(`#${canvasID}`)
      .getBoundingClientRect().height;
    const markerOptions = {
      polygonInPixel: polygon,
      canvasHeight,
      markerWidth: 10,
      markerPadding: 10,
      markerHeight: 5,
      sPadding: 6,
    };
    const topPosition = getMarkerPosition({
      position: 'top',
      ...markerOptions,
    });
    const bottomPosition = getMarkerPosition({
      position: 'bottom',
      ...markerOptions,
    });
    const leftPosition = getMarkerPosition({
      position: 'left',
      ...markerOptions,
    });
    const rightPosition = getMarkerPosition({
      position: 'right',
      ...markerOptions,
    });
    setPrevCalloutPosition(calloutPosition);
    setIndicatorPositions({
      top: topPosition,
      bottom: bottomPosition,
      left: leftPosition,
      right: rightPosition,
    });

    // Add event listeners for mousemove and mouseup
    boundMouseMove = (e) => handleMouseMove(e, isMarker, polygon);
    boundMouseUp = (e) => handleMouseUp(e, elementId, polygon);
    document.addEventListener('mousemove', boundMouseMove);
    document.addEventListener('mouseup', boundMouseUp);
  };

  const handleMouseMove = (e, isMarker, polygon) => {
    if (!isDraggingInProgress) return; //Prevent mousemove until mouse is down
    isMouseMoved.current = true; // Set the ref to true when mouse is moved to restrict the click event
    setDragging(true);

    // Calculate the new position of the element
    const container = document
      .querySelector(`#${canvasID}`)
      .getBoundingClientRect();
    const canvasContainer = document
      .getElementById(`${OCR_CONTAINER_BLOCK}__document-id`)
      .getBoundingClientRect();
    const calloutPreviousPosition = calloutPosition;
    const newRight =
      calloutPreviousPosition === 'right'
        ? container.right - e.clientX + offsetX + calloutWidth
        : container.right - e.clientX + offsetX;
    const newBottom = container.bottom - e.clientY + offsetY;
    const newLeft = e.clientX - container.left - offsetX;

    const draggedX = e.clientX - container.left;
    const draggedY = e.clientY - container.top;

    // Update the position of the element
    const dragStyle = isMarker
      ? { left: `${newLeft}px`, bottom: `${newBottom}px` }
      : { right: `${newRight}px`, bottom: `${newBottom}px` };
    const annotationContainerType = isMarker ? 'marker' : 'callout';

    //Change the cursor style during dragging
    const isWithinValidDropZone =
      (draggedX >= polygon[0] &&
        draggedX <= polygon[2] &&
        draggedY <= polygon[1]) ||
      (draggedX >= polygon[0] &&
        draggedX <= polygon[2] &&
        draggedY >= polygon[7]) ||
      (draggedY >= polygon[1] &&
        draggedY <= polygon[7] &&
        draggedX <= polygon[0]) ||
      (draggedY >= polygon[1] &&
        draggedY <= polygon[7] &&
        draggedX >= polygon[2]);

    const isWithinContainer =
      e.clientX >= canvasContainer.left &&
      e.clientX <= canvasContainer.right - CANVAS_CONTAINER_SCROLLBAR_WIDTH &&
      e.clientY >= canvasContainer.top &&
      e.clientY <= canvasContainer.bottom;

    setCursorType({
      move: isWithinValidDropZone && isWithinContainer,
      notAllowed: !(isWithinValidDropZone && isWithinContainer),
    });
    setDragPosition({
      draggedX: `${draggedX + 10}px`,
      draggedY: `${draggedY - 10}px`,
    });

    if (isWithinContainer) {
      setAnnotationStyle((prevStyle) => ({
        ...prevStyle,
        [annotationContainerType]: {
          ...prevStyle[annotationContainerType],
          ...dragStyle,
        },
      }));
    }
  };

  const handleMouseUp = (e, elementId, polygon) => {
    document.removeEventListener('mousemove', boundMouseMove);
    document.removeEventListener('mouseup', boundMouseUp);
    setDragging(false);
    setCursorType({
      move: false,
      notAllowed: false,
    });
    const canvasContainer = document
      .getElementById(`${OCR_CONTAINER_BLOCK}__document-id`)
      .getBoundingClientRect();
    const isWithinContainer =
      e.clientX >= canvasContainer.left &&
      e.clientX <= canvasContainer.right - CANVAS_CONTAINER_SCROLLBAR_WIDTH &&
      e.clientY >= canvasContainer.top &&
      e.clientY <= canvasContainer.bottom;
    if (!isWithinContainer) {
      setPrevCalloutPosition(null);
      return;
    }
    if (isMouseMoved.current) {
      //If mouse was moved, then set the callout position and hit the API call
      isDraggingInProgress = false;
      offsetX = 0;
      offsetY = 0;

      const container = document
        .querySelector(`#${canvasID}`)
        .getBoundingClientRect();
      const draggedX = e.clientX - container.left - offsetX;
      const draggedY = e.clientY - container.top - offsetY;

      //Reset the cursor style after dragging
      setCursorType({
        move: false,
        notAllowed: false,
      });

      const calloutPositionId = getDraggedCalloutPosition({
        draggedX,
        draggedY,
        polygon,
      });

      // If the elements are selected in batch, we need to update the dragged callouts for all selected elements
      if (
        selectedElementIds.length &&
        selectedElementIds.includes(elementId) &&
        calloutPositionId
      ) {
        const batchPayload = {};
        const batchPayloadForElementCache = {};
        selectedElementIds.forEach((id) => {
          if (hasAnnotations(selectedElementsMap[id])) {
            batchPayload[id] = calloutPositionId;
            batchPayloadForElementCache[id] =
              REVERSE_ANNOTATION_POSITION_TYPES[calloutPositionId];
          }
        });
        dispatch(
          updateElementCacheByCalloutPosition({
            elementCalloutPosition: batchPayloadForElementCache,
          }),
        );
        saveCalloutPositionsInDB(batchPayload);
      } else if (calloutPositionId) {
        // If the element is not selected in batch, we update the dragged callout for the specific element
        dispatch(
          updateElementCacheByCalloutPosition({
            elementCalloutPosition: {
              [elementId]: REVERSE_ANNOTATION_POSITION_TYPES[calloutPositionId],
            },
          }),
        );

        saveCalloutPositionsInDB({
          [elementId]: calloutPositionId,
        });
      }
    }
  };

  const cursorClassName = cursorType.move
    ? `${BLOCK}__annotation-cursor--move`
    : cursorType.notAllowed
    ? `${BLOCK}__annotation-cursor--not-allowed`
    : `${BLOCK}__annotation-cursor--pointer`;

  const totalElementDragged =
    selectedElementIds.length && selectedElementIds.includes(elementId)
      ? selectedElementIds.filter((id) =>
          hasAnnotations(selectedElementsMap[id]),
        ).length
      : 1;
  return (
    <>
      {isDragging && (
        <div
          className={`${BLOCK}__total-elements-dragging-circle`}
          style={{ top: dragPosition.draggedY, left: dragPosition.draggedX }}
        >
          <span className={`${BLOCK}__total-elements-count`}>
            {totalElementDragged}
          </span>
        </div>
      )}
      {isDragging && (
        <>
          <DropIndicator
            position={'top'}
            left={indicatorPositions?.top?.left}
            bottom={indicatorPositions?.top?.bottom}
          />
          <DropIndicator
            position={'right'}
            left={indicatorPositions?.right?.left}
            bottom={indicatorPositions?.right?.bottom}
          />
          <DropIndicator
            position={'bottom'}
            left={indicatorPositions?.bottom?.left}
            bottom={indicatorPositions?.bottom?.bottom}
          />
          <DropIndicator
            position={'left'}
            left={indicatorPositions?.left?.left}
            bottom={indicatorPositions?.left?.bottom}
          />
        </>
      )}
      {annotation.marker && !elementDetails.callout && (
        <div
          id={elementId}
          style={{
            display: 'flex',
            position: 'absolute',
            ...annotationStyle.marker,
            width: 'fit-content',
            zIndex: 49,
          }}
          className={classNames({
            [`${BLOCK}__annotation-indicator--left`]:
              calloutPosition === 'left',
            [`${BLOCK}__annotation-indicator--right`]:
              calloutPosition === 'right',
            [`${BLOCK}__annotation-indicator--bottom`]:
              calloutPosition === 'bottom',
            [`${cursorClassName}`]: !!cursorClassName,
          })}
          onMouseDown={(e) => {
            handleMouseDown(e, true, elementId, polygonInPixel);
            e.stopPropagation();
          }}
          onClick={() => {
            !isPreviewPage && handleIndicatorClick(elementId);
          }}
        >
          <AnnotationIndicator
            width={ANNOTATION_INDICATOR_WIDTH}
            height={ANNOTATION_INDICATOR_HEIGHT}
          />
        </div>
      )}
      {annotation.callout && (
        <div>
          <AnnotationCalloutComponent
            elementId={elementId}
            BLOCK={BLOCK}
            position={calloutPosition}
            maxWidth={`${width}px`}
            customStyle={annotationStyle.callout}
            onClick={() => {
              handleIndicatorClick(elementId);
            }}
            fontSize={
              elementDetails && elementDetails.display === '―'
                ? polygonHeight
                : polygonHeight - 3
            }
            handleMouseDown={handleMouseDown}
            polygonInPixel={polygonInPixel}
            batchElementList={batchElementList}
            cursorClassName={cursorClassName}
          >
            {children}
          </AnnotationCalloutComponent>
        </div>
      )}
    </>
  );
};

export default OcrCallouts;
